import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Button as MuiButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MainContainer } from '../../../components/ContentWrapper';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { PageTitle } from '../../../components/PageTitle';
import { Button } from '../../../components/Button/Button';
import { DotLottleLoader } from '../../../components/Loader';
import { IconPlus, IconPencil, IconTrash } from '../../../utils/icons';
import config from '../../../config';
import axiosInstant from '../../../api';
import {
  adminAttributes,
  adminAttributeValues,
} from '../../../api/endpoints';
import { ConfirmDialog } from '../expertMatching/ConfirmDialog';
import { AttributeForm } from './AttributeForm';
import { AttributeValueForm } from './AttributeValueForm';
import { IAttribute, IAttributeValue } from './constants';

const Attributes = () => {
  const [data, setData] = useState<IAttribute[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [saving, setSaving] = useState(false);

  // Attribute create/edit + delete.
  const [showAttrForm, setShowAttrForm] = useState(false);
  const [selectedAttr, setSelectedAttr] = useState<IAttribute | undefined>();
  const [attrToDelete, setAttrToDelete] = useState<IAttribute | undefined>();

  // Option (attribute_value) create/edit + delete. `valueFormFor` holds the
  // owning attribute; `selectedValue` is set when editing.
  const [valueFormFor, setValueFormFor] = useState<IAttribute | undefined>();
  const [selectedValue, setSelectedValue] = useState<IAttributeValue | undefined>();
  const [valueToDelete, setValueToDelete] = useState<IAttributeValue | undefined>();

  const attrUrl = `${config.API_URL}${adminAttributes}`;
  const valueUrl = `${config.API_URL}${adminAttributeValues}`;

  const fetchData = async () => {
    try {
      const res = await axiosInstant.get(attrUrl);
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Failed to load attributes');
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    setShowLoader(true);
    fetchData();
  }, []);

  const sorted = useMemo(
    () =>
      [...data].sort(
        (a, b) => a.order - b.order || a.name.localeCompare(b.name),
      ),
    [data],
  );

  // --- Attribute CRUD -----------------------------------------------------
  const closeAttrForm = () => {
    setSelectedAttr(undefined);
    setShowAttrForm(false);
  };

  const submitAttribute = async (payload: Partial<IAttribute>) => {
    setSaving(true);
    try {
      if (selectedAttr) {
        await axiosInstant.patch(`${attrUrl}/${selectedAttr.id}`, payload);
        toast.success('Attribute updated');
      } else {
        await axiosInstant.post(attrUrl, payload);
        toast.success('Attribute created');
      }
      await fetchData();
      closeAttrForm();
    } catch (err) {
      toast.error('Failed to save attribute');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteAttribute = async () => {
    if (!attrToDelete) return;
    setSaving(true);
    try {
      await axiosInstant.delete(`${attrUrl}/${attrToDelete.id}`);
      toast.success('Attribute deleted');
      await fetchData();
    } catch (err) {
      toast.error('Failed to delete attribute');
    } finally {
      setSaving(false);
      setAttrToDelete(undefined);
    }
  };

  // --- Option (attribute_value) CRUD --------------------------------------
  const closeValueForm = () => {
    setSelectedValue(undefined);
    setValueFormFor(undefined);
  };

  const submitValue = async (payload: Partial<IAttributeValue>) => {
    if (!valueFormFor) return;
    setSaving(true);
    try {
      if (selectedValue) {
        await axiosInstant.patch(`${valueUrl}/${selectedValue.id}`, payload);
        toast.success('Option updated');
      } else {
        await axiosInstant.post(valueUrl, {
          ...payload,
          attribute_id: valueFormFor.id,
        });
        toast.success('Option created');
      }
      await fetchData();
      closeValueForm();
    } catch (err) {
      toast.error('Failed to save option');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteValue = async () => {
    if (!valueToDelete) return;
    setSaving(true);
    try {
      await axiosInstant.delete(`${valueUrl}/${valueToDelete.id}`);
      toast.success('Option deleted');
      await fetchData();
    } catch (err) {
      toast.error('Failed to delete option');
    } finally {
      setSaving(false);
      setValueToDelete(undefined);
    }
  };

  return (
    <WhiteBackground>
      <MainContainer>
        <Box className="w-full flex flex-wrap justify-between items-center gap-3 pb-8">
          <PageTitle title="Attributes" />
          <Button
            className="!flex gap-2 !px-4"
            onClick={() => setShowAttrForm(true)}
          >
            <IconPlus />
            <Typography>New Attribute</Typography>
          </Button>
        </Box>

        <Box className="relative">
          {showLoader && <DotLottleLoader />}
          {!showLoader &&
            sorted.map((attr) => (
              <Accordion key={attr.id} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box className="flex items-center justify-between w-full pr-3 gap-2">
                    <Box className="flex items-center gap-2 flex-wrap">
                      <Typography fontWeight={600}>{attr.name}</Typography>
                      <Chip
                        size="small"
                        label={`${attr.values?.length ?? 0} options`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`weight ${attr.default_weight}`}
                      />
                    </Box>
                    <Box className="flex items-center gap-1">
                      <IconButton
                        size="small"
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAttr(attr);
                          setShowAttrForm(true);
                        }}
                      >
                        <IconPencil size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        component="span"
                        sx={{ color: 'var(--text)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAttrToDelete(attr);
                        }}
                      >
                        <IconTrash width={18} height={18} stroke="currentColor" />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className="flex justify-end pb-2">
                    <MuiButton
                      size="small"
                      variant="outlined"
                      sx={{ '& .MuiButton-startIcon': { color: 'var(--text)' } }}
                      startIcon={<IconPlus width={16} height={16} stroke="currentColor" />}
                      onClick={() => {
                        setSelectedValue(undefined);
                        setValueFormFor(attr);
                      }}
                    >
                      Add Option
                    </MuiButton>
                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Option</TableCell>
                        <TableCell>Slug</TableCell>
                        <TableCell align="center">Order</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(attr.values ?? [])
                        .slice()
                        .sort(
                          (a, b) =>
                            a.order - b.order || a.name.localeCompare(b.name),
                        )
                        .map((val) => (
                          <TableRow key={val.id} hover>
                            <TableCell>{val.name}</TableCell>
                            <TableCell>{val.slug}</TableCell>
                            <TableCell align="center">{val.order}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedValue(val);
                                  setValueFormFor(attr);
                                }}
                              >
                                <IconPencil size={16} />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ color: 'var(--text)' }}
                                onClick={() => setValueToDelete(val)}
                              >
                                <IconTrash width={16} height={16} stroke="currentColor" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      {(attr.values ?? []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography className="py-3" color="var(--text-2)">
                              No options yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          {!showLoader && sorted.length === 0 && (
            <Typography className="py-6" color="var(--text-2)">
              No attributes yet.
            </Typography>
          )}
        </Box>
      </MainContainer>

      {showAttrForm && (
        <AttributeForm
          data={selectedAttr}
          isLoading={saving}
          onSubmit={submitAttribute}
          onClose={closeAttrForm}
        />
      )}

      {valueFormFor && (
        <AttributeValueForm
          attribute={valueFormFor}
          data={selectedValue}
          isLoading={saving}
          onSubmit={submitValue}
          onClose={closeValueForm}
        />
      )}

      <ConfirmDialog
        open={!!attrToDelete}
        title="Delete attribute"
        message={`Delete "${attrToDelete?.name}" and all of its options? This affects expert matching until scores are recomputed.`}
        loading={saving}
        onCancel={() => setAttrToDelete(undefined)}
        onConfirm={confirmDeleteAttribute}
      />

      <ConfirmDialog
        open={!!valueToDelete}
        title="Delete option"
        message={`Delete option "${valueToDelete?.name}"?`}
        loading={saving}
        onCancel={() => setValueToDelete(undefined)}
        onConfirm={confirmDeleteValue}
      />
    </WhiteBackground>
  );
};

export default Attributes;
